/**
 * Event budget CSV: one file that carries the same numbers as the screen and the Excel export.
 *
 *   ID,Category,Planned,Spent,Remaining,Currency,Notes     one row per category, then a Total row
 *   Expenses                                                a marker row
 *   ID,Date,Description,Type,...                            every cost entry, in the shared expenses layout
 *
 * Importing the file again restores the planned lines and the expenses (Spent and Remaining are calculated, so
 * they are read as output only). A plain planned-only CSV (the older export or the blank template) still imports.
 * Pure; safe in Node and the browser.
 */
import { csvCell, type CsvValue } from "../csv";
import { budgetReport } from "./budgetOps";
import { parseCsv } from "./csvRead";
import { exportRecords, importRecords, type ImportIssue } from "./layouts";
import type { BudgetLine, Expense } from "./types";

export const EXPENSES_MARKER = "Expenses";
const HEAD = ["ID", "Category", "Planned", "Spent", "Remaining", "Currency", "Notes"];
const line = (cells: CsvValue[]) => cells.map(csvCell).join(",");

export function exportBudgetCsv(budgets: readonly BudgetLine[], expenses: readonly Expense[], currency: string): string {
  const rep = budgetReport(budgets, expenses, currency);
  const notes = new Map(budgets.map((b) => [b.id, b.notes ?? ""]));
  const out: string[] = [line(HEAD)];
  for (const r of rep.rows) out.push(line([r.lineId ?? "", r.category, r.planned, r.spent, r.remaining, currency, r.lineId ? notes.get(r.lineId) : ""]));
  // Lines in another currency are not in the totals (as on screen) but are kept so nothing is lost.
  for (const b of budgets) if (b.currency !== currency) out.push(line([b.id, b.category, b.planned, "", "", b.currency, b.notes ?? ""]));
  out.push(line(["", "Total", rep.planned, rep.spent, rep.remaining, currency, ""]));
  const costs = expenses.filter((e) => e.kind === "cost");
  if (costs.length) {
    out.push(line([EXPENSES_MARKER]));
    out.push(exportRecords("expenses", costs).replace(/^﻿/, "").trimEnd());
  }
  return "﻿" + out.join("\r\n") + "\r\n";
}

export interface BudgetCsvImport {
  budgets: BudgetLine[];
  expenses: Expense[];
  issues: ImportIssue[];
  rowsRead: number;
  rowsSkipped: number;
}

const rowsToText = (rows: string[][]) => rows.map((r) => line(r)).join("\r\n") + "\r\n";

export function importBudgetCsv(text: string, ctx: { currency: string }): BudgetCsvImport {
  const rows = parseCsv(text);
  const mark = rows.findIndex((r, i) => i > 0 && r[0]?.trim().toLowerCase() === EXPENSES_MARKER.toLowerCase() && r.slice(1).every((c) => !c.trim()));
  let budgetRows = mark === -1 ? rows : rows.slice(0, mark);
  const expenseRows = mark === -1 ? [] : rows.slice(mark + 1);

  // Our own export: drop the calculated columns and the rows that are not planned lines (Total, unplanned spend).
  const head = budgetRows[0]?.map((h) => h.trim().toLowerCase()) ?? [];
  const spentAt = head.indexOf("spent"), remAt = head.indexOf("remaining");
  if (spentAt !== -1) {
    const idAt = head.indexOf("id"), catAt = head.indexOf("category"), planAt = head.indexOf("planned");
    const cell = (r: string[], i: number) => (i === -1 ? "" : (r[i] ?? "").trim());
    budgetRows = budgetRows.filter((r, i) => {
      if (i === 0) return true;
      if (cell(r, idAt)) return true;
      if (cell(r, catAt).toLowerCase() === "total") return false;
      return !(Number(cell(r, planAt) || 0) === 0 && Number(cell(r, spentAt)) > 0);
    }).map((r) => r.filter((_, i) => i !== spentAt && i !== remAt));
  }

  const b = importRecords("budgets", rowsToText(budgetRows), ctx);
  const e = expenseRows.length ? importRecords("expenses", rowsToText(expenseRows), ctx) : null;
  return {
    budgets: b.records,
    expenses: e ? e.records : [],
    issues: [...b.issues, ...(e ? e.issues.map((i) => ({ ...i, message: `(expenses) ${i.message}` })) : [])],
    rowsRead: b.rowsRead + (e?.rowsRead ?? 0),
    rowsSkipped: b.rowsSkipped + (e?.rowsSkipped ?? 0),
  };
}
